import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import FooterNav from '../components/ui/footer-nav';
import NavMenu from '../components/ui/nav-menu';
import HeaderTasksButton from '../components/ui/header-tasks-button';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { MicrophoneHelpLink } from '../components/ui/microphone-help';
import { 
  Mic, Camera, Paperclip, Users, Briefcase,
  Building2, ChevronDown, Plus, Send,
  UserPlus, FileEdit, Calculator, FileSpreadsheet,
  ClipboardCheck, ShieldCheck, GitBranch, UserCheck,
  FileText, Banknote, Bell, CheckCircle,
  Presentation, Award, CircleCheck, Sparkles, Mail,
  Trophy, Truck
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TradieHomeNew = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [counts, setCounts] = useState({ leads: 0, updates: 0, claims: 0 });
  const [activeCard, setActiveCard] = useState(1); // 0=Lead, 1=Site Update, 2=Claim
  const carouselRef = useRef(null);
  
  // Active module tabs for each workspace
  const [winActiveModule, setWinActiveModule] = useState('lead');
  const [deliverActiveModule, setDeliverActiveModule] = useState('update');
  const [getPaidActiveModule, setGetPaidActiveModule] = useState('claim');
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState('');

  // Module definitions for each workspace
  const winModules = [
    { id: 'lead', label: 'Lead / Referral', icon: UserPlus, path: '/tradie/leads-referral' },
    { id: 'scope', label: 'Generate Scope', icon: FileEdit, path: '/tradie/capture-job-notes' },
    { id: 'costing', label: 'Costing', icon: Calculator, path: null },
    { id: 'quote', label: 'Quote', icon: FileSpreadsheet, path: null },
    { id: 'proposal', label: 'Proposal', icon: Presentation, path: null },
    { id: 'create-win', label: 'Create Your Own', icon: Plus, path: null },
  ];

  const deliverModules = [
    { id: 'update', label: 'Site Update', icon: ClipboardCheck, path: '/tradie/log-update' },
    { id: 'swms', label: 'SWMS', icon: ShieldCheck, path: '/tradie/generate-swms' },
    { id: 'signoff', label: 'Record Sign-Off', icon: UserCheck, path: null },
    { id: 'variation', label: 'Variation', icon: GitBranch, path: null },
    { id: 'certificate', label: 'Certificate of Compliance', icon: Award, path: null },
    { id: 'completion', label: 'Job Completion', icon: CircleCheck, path: null },
    { id: 'create-deliver', label: 'Create Your Own', icon: Plus, path: null },
  ];

  const getPaidModules = [
    { id: 'claim', label: 'Claim / Invoice', icon: FileText, path: '/tradie/claim' },
    { id: 'payment', label: 'Log Payment', icon: Banknote, path: null },
    { id: 'reminder', label: 'Send Reminder', icon: Bell, path: null },
    { id: 'create-paid', label: 'Create Your Own', icon: Plus, path: null },
  ];

  useEffect(() => {
    fetchCounts();
  }, []);

  useEffect(() => {
    // Scroll to center card (Site Update) on load
    if (carouselRef.current) {
      const containerWidth = carouselRef.current.offsetWidth;
      carouselRef.current.scrollLeft = containerWidth;
    }
  }, []);

  const fetchCounts = async () => {
    try {
      const [updatesRes, leadsRes] = await Promise.all([
        axios.get(`${API_URL}/updates/my`),
        axios.get(`${API_URL}/leads/my`)
      ]);
      setCounts({
        leads: leadsRes.data.length,
        updates: updatesRes.data.length,
        claims: 0
      });
    } catch (error) {
      console.error('Failed to fetch counts:', error);
    }
  };

  const modules = [
    {
      id: 'lead',
      title: 'Lead / Referral',
      stage: 'WIN WORK',
      intro: 'Capture leads and referrals fast.',
      placeholder: "What's the opportunity?",
      stageColor: 'text-emerald-400',
      barColor: 'bg-emerald-500',
      micColor: 'bg-emerald-600 border-emerald-400',
      buttonColor: 'bg-emerald-600 hover:bg-emerald-700',
      accentColor: 'text-emerald-600',
      path: '/tradie/leads-referral',
      icon: Trophy
    },
    {
      id: 'site-update',
      title: 'Site Update',
      stage: 'DELIVER WORK',
      intro: 'Capture site progress fast.',
      placeholder: "What's happening on site?",
      stageColor: 'text-blue-400',
      barColor: 'bg-blue-500',
      micColor: 'bg-blue-600 border-blue-400',
      buttonColor: 'bg-blue-600 hover:bg-blue-700',
      accentColor: 'text-blue-600',
      path: '/tradie/log-update',
      icon: Truck
    },
    {
      id: 'claim',
      title: 'Claim / Invoice',
      stage: 'GET PAID',
      intro: 'Capture what needs to be claimed.',
      placeholder: "What needs to be claimed?",
      stageColor: 'text-orange-400',
      barColor: 'bg-orange-500',
      micColor: 'bg-orange-600 border-orange-400',
      buttonColor: 'bg-orange-600 hover:bg-orange-700',
      accentColor: 'text-orange-600',
      path: '/tradie/claim',
      icon: Banknote
    }
  ];

  const handleScroll = () => {
    if (carouselRef.current) {
      const scrollPos = carouselRef.current.scrollLeft;
      const containerWidth = carouselRef.current.offsetWidth;
      const newActive = Math.round(scrollPos / containerWidth);
      if (newActive !== activeCard) {
        setActiveCard(Math.max(0, Math.min(2, newActive)));
      }
    }
  };

  const scrollToCard = (index) => {
    if (carouselRef.current) {
      const containerWidth = carouselRef.current.offsetWidth;
      carouselRef.current.scrollTo({
        left: index * containerWidth,
        behavior: 'smooth'
      });
    }
  };

  const activeModule = modules[activeCard];

  // Lead/Referral structured fields
  const entryTypes = [
    { value: 'lead', label: 'Job Lead' },
    { value: 'referral_received', label: 'Referral Received' },
    { value: 'referral_given', label: 'Referral Given' }
  ];
  const workTypes = [
    { value: 'solar', label: 'Solar' }, { value: 'electrical', label: 'Electrical' },
    { value: 'rendering', label: 'Rendering' }, { value: 'maintenance', label: 'Maintenance' },
    { value: 'plumbing', label: 'Plumbing' }, { value: 'fitout', label: 'Fitout' },
    { value: 'other', label: 'Other' }
  ];
  const sources = [
    { value: 'existing_builder', label: 'Existing Builder' }, { value: 'existing_client', label: 'Existing Client' },
    { value: 'direct_enquiry', label: 'Direct Enquiry' }, { value: 'referral', label: 'Referral' },
    { value: 'partner_introducer', label: 'Partner / Introducer' }, { value: 'other', label: 'Other' }
  ];

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Dynamic Header - changes with active card */}
      <header className="bg-[#1F2A37] text-white px-4 py-3 sticky top-0 z-10 relative">
        <div className={`h-1 ${activeModule.barColor} absolute top-0 left-0 right-0 transition-colors duration-300`}></div>
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {activeModule.icon && <activeModule.icon className={`w-7 h-7 ${activeModule.stageColor}`} />}
            <div>
              <h1 className="text-xl font-bold">{getGreeting()}, {user?.name?.split(' ')[0] || 'there'}</h1>
              <p className={`${activeModule.stageColor} text-xs font-semibold uppercase tracking-wide transition-colors duration-300`}>{activeModule.stage}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <HeaderTasksButton stage={activeCard === 0 ? 'win' : activeCard === 1 ? 'deliver' : 'get-paid'} />
            <NavMenu />
          </div>
        </div>
      </header>

      {/* Full-width Swipeable Module Cards - Native Scroll */}
      <div className="relative">
        <div 
          ref={carouselRef}
          onScroll={handleScroll}
          className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide"
          style={{ scrollSnapType: 'x mandatory' }}
        >
          {modules.map((module, index) => {
            // Get the appropriate module rail for each workspace
            const moduleRail = index === 0 ? winModules : index === 1 ? deliverModules : getPaidModules;
            const activeModuleId = index === 0 ? winActiveModule : index === 1 ? deliverActiveModule : getPaidActiveModule;
            const setActiveModuleId = index === 0 ? setWinActiveModule : index === 1 ? setDeliverActiveModule : setGetPaidActiveModule;
            const accentColor = index === 0 ? 'emerald' : index === 1 ? 'blue' : 'orange';
            const borderColor = index === 0 ? '#10b981' : index === 1 ? '#3b82f6' : '#f97316';
            const textColor = index === 0 ? '#059669' : index === 1 ? '#2563eb' : '#ea580c';

            return (
              <div
                key={module.id}
                data-testid={`module-card-${module.id}`}
                className="flex-shrink-0 w-full snap-center"
                style={{ scrollSnapAlign: 'center', minWidth: '100%' }}
              >
                {/* Module Rail */}
                <div className="bg-white border-b border-slate-200">
                  <div className="max-w-lg mx-auto overflow-x-auto scrollbar-hide">
                    <div className="flex">
                      {moduleRail.map((mod) => {
                        const Icon = mod.icon;
                        const isActive = activeModuleId === mod.id;
                        const isPlaceholder = !mod.path;
                        return (
                          <button
                            key={mod.id}
                            onClick={() => {
                              if (mod.path) {
                                setActiveModuleId(mod.id);
                                navigate(mod.path);
                              } else {
                                setSelectedFeature(mod.label);
                                setShowContactModal(true);
                              }
                            }}
                            className={`
                              flex items-center gap-2 px-4 py-3 border-b-2 whitespace-nowrap transition-colors
                              ${isActive ? 'border-current' : 'border-transparent text-slate-500'}
                              ${isPlaceholder ? 'opacity-60 hover:opacity-80' : 'hover:opacity-80'}
                            `}
                            style={isActive ? { borderColor: borderColor, color: textColor } : {}}
                          >
                            <Icon className="w-4 h-4" />
                            <span className="text-sm font-medium">{mod.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Main Content - mirrors inside module exactly */}
                <main className="p-4 max-w-lg mx-auto">
                  <Card className="border-2 border-slate-200">
                    <CardContent className="p-4 space-y-4">
                      {/* Intro Text */}
                      <p className="text-slate-600 text-base">{module.intro}</p>

                      {/* Centered Microphone */}
                      <div className="text-center py-1">
                        <button 
                          type="button"
                          onClick={() => navigate(module.path)}
                          className={`w-20 h-20 mx-auto flex items-center justify-center rounded-full transition-all border-4 shadow-lg ${module.micColor} active:scale-95`}
                        >
                          <Mic className="w-8 h-8 text-white" strokeWidth={2} />
                        </button>
                        <p className="text-slate-600 text-sm font-medium mt-2">Speak or type</p>
                        <MicrophoneHelpLink className="mt-0" />
                      </div>

                      {/* Text Input */}
                      <Textarea 
                        placeholder={module.placeholder}
                      className="min-h-[80px] text-base border-2 cursor-pointer" 
                      onClick={() => navigate(module.path)}
                      readOnly
                    />

                    {/* Photo / Attachment - Right aligned */}
                    <div className="flex items-center justify-end gap-2">
                      <button 
                        type="button" 
                        onClick={() => navigate(module.path)}
                        className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-50"
                      >
                        <Camera className="w-4 h-4" /> Photo
                      </button>
                      <button 
                        type="button" 
                        onClick={() => navigate(module.path)}
                        className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-50"
                      >
                        <Paperclip className="w-4 h-4" /> Attach
                      </button>
                    </div>

                    {/* Structured Details Section - Module Specific */}
                    <div className="pt-2 border-t border-slate-200">
                      <p className="text-xs text-slate-500 mb-3 font-medium uppercase tracking-wide">Add details (optional)</p>

                      {/* Lead/Referral Fields */}
                      {module.id === 'lead' && (
                        <>
                          <div className="space-y-1 mb-3">
                            <Label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                              <Users className={`w-4 h-4 ${module.accentColor}`} strokeWidth={2} /> Lead or Referral?
                            </Label>
                            <Select onOpenChange={() => navigate(module.path)}>
                              <SelectTrigger className="h-11 border-2"><SelectValue placeholder="Choose type" /></SelectTrigger>
                              <SelectContent>{entryTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1 mb-3">
                            <Label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                              <Briefcase className={`w-4 h-4 ${module.accentColor}`} strokeWidth={2} /> Work type
                            </Label>
                            <Select onOpenChange={() => navigate(module.path)}>
                              <SelectTrigger className="h-11 border-2"><SelectValue placeholder="Type of work" /></SelectTrigger>
                              <SelectContent>{workTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1 mb-3">
                            <Label className="text-sm font-bold text-slate-700">Source</Label>
                            <Select onOpenChange={() => navigate(module.path)}>
                              <SelectTrigger className="h-11 border-2"><SelectValue placeholder="Where did this come from?" /></SelectTrigger>
                              <SelectContent>{sources.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div 
                            className="border-2 border-slate-200 rounded-lg p-3 cursor-pointer hover:bg-slate-50"
                            onClick={() => navigate(module.path)}
                          >
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-slate-500" strokeWidth={2} />
                              <span className="text-sm font-semibold text-slate-700">Project details</span>
                              <ChevronDown className="w-4 h-4 text-slate-400 ml-auto" strokeWidth={2} />
                            </div>
                          </div>
                        </>
                      )}

                      {/* Site Update Fields */}
                      {module.id === 'site-update' && (
                        <>
                          <div className="space-y-1 mb-3">
                            <Label className="text-sm font-bold text-slate-700">Job name</Label>
                            <Input 
                              placeholder="Project or site name" 
                              className="h-11 border-2 cursor-pointer" 
                              onClick={() => navigate(module.path)}
                              readOnly
                            />
                          </div>
                          <div className="border-2 border-slate-200 rounded-lg overflow-hidden">
                            <button 
                              type="button" 
                              onClick={() => navigate(module.path)}
                              className="w-full flex items-center justify-between p-3 text-left bg-white hover:bg-slate-50"
                            >
                              <div className="flex items-center gap-2">
                                <Plus className="w-4 h-4 text-slate-500" strokeWidth={2} />
                                <span className="text-sm font-semibold text-slate-700">More details</span>
                              </div>
                              <ChevronDown className="w-4 h-4 text-slate-400" strokeWidth={2} />
                            </button>
                          </div>
                        </>
                      )}

                      {/* Claim/Invoice Fields */}
                      {module.id === 'claim' && (
                        <div className="border-2 border-slate-200 rounded-lg overflow-hidden">
                          <button 
                            type="button" 
                            onClick={() => navigate(module.path)}
                            className="w-full flex items-center justify-between p-3 text-left bg-white hover:bg-slate-50"
                          >
                            <div className="flex items-center gap-2">
                              <Plus className="w-4 h-4 text-slate-500" strokeWidth={2} />
                              <span className="text-sm font-semibold text-slate-700">Job & Payment details</span>
                            </div>
                            <ChevronDown className="w-4 h-4 text-slate-400" strokeWidth={2} />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Submit Button */}
                    <Button 
                      type="button" 
                      onClick={() => navigate(module.path)}
                      className={`w-full h-12 ${module.buttonColor} text-white text-base font-bold`}
                    >
                      <Send className="w-5 h-5 mr-2" strokeWidth={2} />
                      {module.id === 'lead' ? 'Generate Summary' : module.id === 'site-update' ? 'Submit Update' : 'Capture Claim'}
                    </Button>
                  </CardContent>
                </Card>
              </main>
            </div>
          )})}
        </div>

        {/* Dot Indicators - clickable */}
        <div className="flex items-center justify-center gap-2 py-3">
          {modules.map((module, index) => (
            <button
              key={module.id}
              onClick={() => scrollToCard(index)}
              className={`h-2 rounded-full transition-all ${
                activeCard === index 
                  ? `w-6 ${module.barColor}` 
                  : 'w-2 bg-slate-300'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Footer Navigation */}
      <FooterNav counts={counts} />

      {/* Feature Contact Modal */}
      {/* Feature Contact Modal */}
      <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
        <DialogContent className="sm:max-w-sm mx-4 rounded-2xl border-0 p-0 overflow-hidden">
          {/* Compact header with gradient */}
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 px-6 py-5">
            <div className="flex items-center justify-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white">
                {selectedFeature}
              </h2>
            </div>
          </div>
          
          {/* Body */}
          <div className="px-6 py-5">
            <p className="text-slate-600 text-center text-sm mb-5">
              This feature can be added to your 2Tap setup. Get in touch with the team to discuss your requirements.
            </p>
            
            <div className="flex flex-col gap-3">
              <Button
                className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl"
                onClick={() => {
                  setShowContactModal(false);
                  const subject = encodeURIComponent(`2Tap Feature Request: ${selectedFeature}`);
                  const body = encodeURIComponent(`Hi 2Tap Team,

I'm interested in the **${selectedFeature}** feature in 2Tap.

Here are my details:
• Name: ${user?.name || 'N/A'}
• Email: ${user?.email || 'N/A'}

Appreciate it — keen to hear what's possible.

Thanks 2Tap!`);
                  window.location.href = `mailto:tradeofficeai@gmail.com?subject=${subject}&body=${body}`;
                }}
              >
                <Mail className="w-5 h-5 mr-2" />
                Contact 2Tap Team
              </Button>
              <Button
                variant="ghost"
                className="w-full h-10 text-slate-500 hover:text-slate-700 font-medium"
                onClick={() => setShowContactModal(false)}
              >
                Maybe later
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TradieHomeNew;
